import { useRouter } from 'next/navigation';
import React from 'react';
import { useUserCreateScenario } from '@/domain/userCreate/UserCreateScenarioGuard/ScenarioDataContextProvider';
import type { UserCreate_ValidatePasswordQuery } from '@/gql/__codegen__/graphql';
import { useSessionStorageEx } from '@/localShared/hooks/useSessionStorageEx';
import { errorMessagesHelper } from '@/shared/helpers/errorMessagesHelper';
import { useSendEmailVerificationCodeMutation } from './hooks/useSendEmailVerificationCodeMutation';
import { useValidatePasswordQuery } from './hooks/useValidatePasswordQuery';

type State = {
  pageStatus: 'INPUT' | 'VALIDATE_PASSWORD' | 'SEND_EMAIL';
  errorMessage: string;
  formValues: {
    mailAddress: string;
    password: string;
  };
};

type Action =
  | {
      type: 'START_PASSWORD_VALIDATION';
      payload: {
        formValues: State['formValues'];
      };
    }
  | {
      type: 'COMPLETE_PASSWORD_VALIDATION';
      payload: {
        data: UserCreate_ValidatePasswordQuery;
      };
    }
  | {
      type: 'ERROR';
      payload: {
        errorMessage: string;
      };
    };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'START_PASSWORD_VALIDATION': {
      const { formValues } = action.payload;

      return {
        ...state,
        pageStatus: 'VALIDATE_PASSWORD',
        formValues,
      };
    }

    case 'COMPLETE_PASSWORD_VALIDATION': {
      const {
        validatePassword: { userErrors, valid },
      } = action.payload.data;

      if (userErrors.length > 0) {
        switch (userErrors[0]?.code) {
          case 'NOT_SECURE': {
            return {
              ...state,
              pageStatus: 'INPUT',
              errorMessage: `パスワードは英数字両方を含む必要があります（${userErrors[0]?.code}）`,
            };
          }
          default: {
            return {
              ...state,
              pageStatus: 'INPUT',
              errorMessage: userErrors[0].message,
            };
          }
        }
      }

      if (!valid) {
        return {
          ...state,
          pageStatus: 'INPUT',
          errorMessage: 'このパスワードは利用できません。',
        };
      }

      return {
        ...state,
        pageStatus: 'SEND_EMAIL',
      };
    }

    case 'ERROR': {
      return {
        ...state,
        errorMessage: action.payload.errorMessage,
        pageStatus: 'INPUT',
      };
    }
  }
};

export type UseUserCreateStart = {
  state: State;
  loading: boolean;
  sendVerificationCode: (formValues: State['formValues']) => void;
};

export function useUserCreateStart(): UseUserCreateStart {
  const { value: userCreateValue, setValue: setUserCreateValue } =
    useSessionStorageEx('userCreate');

  const [state, dispatch] = React.useReducer(reducer, {
    pageStatus: 'INPUT',
    errorMessage: '',
    formValues: {
      mailAddress: userCreateValue?.mailAddress ?? '',
      password: userCreateValue?.password ?? '',
    },
  });

  const router = useRouter();

  const {
    scenarioId,
    data: { isRequiredEmailVerification },
  } = useUserCreateScenario();

  useValidatePasswordQuery({
    skip: state.pageStatus !== 'VALIDATE_PASSWORD',
    fetchPolicy: 'no-cache',
    variables: {
      password: state.formValues.password,
    },
    onCompleted: (data) => {
      dispatch({
        type: 'COMPLETE_PASSWORD_VALIDATION',
        payload: {
          data,
        },
      });
    },
    onError(error) {
      dispatch({
        type: 'ERROR',
        payload: {
          errorMessage: errorMessagesHelper.graphql(error),
        },
      });
    },
  });

  const [sendEmailMutation, { loading }] = useSendEmailVerificationCodeMutation(
    {
      onCompleted: ({ sendEmailVerificationCode }) => {
        if (
          !sendEmailVerificationCode?.hash ||
          !sendEmailVerificationCode?.token
        ) {
          dispatch({
            type: 'ERROR',
            payload: {
              errorMessage: errorMessagesHelper.unexpected(),
            },
          });
          return;
        }

        const { token, hash } = sendEmailVerificationCode;
        router.push(
          `/user-create/confirm/${scenarioId}?token=${token}&hash=${hash}`
        );
      },
      onError: (error) => {
        dispatch({
          type: 'ERROR',
          payload: {
            errorMessage: errorMessagesHelper.graphql(
              error,
              'sendEmailVerificationCode'
            ),
          },
        });
      },
    }
  );

  const sendVerificationCode: UseUserCreateStart['sendVerificationCode'] =
    React.useCallback(
      (formValues) => {
        // 次の画面で使うので、ここでsaveしてしまう
        setUserCreateValue(formValues);

        dispatch({
          type: 'START_PASSWORD_VALIDATION',
          payload: {
            formValues,
          },
        });
      },
      [setUserCreateValue]
    );

  React.useEffect(() => {
    if (state.pageStatus !== 'SEND_EMAIL') return;

    // メール認証しない場合、さっさと次の画面へ
    if (!isRequiredEmailVerification) {
      router.push(`/user-create/challenge/${scenarioId}`);
      return;
    }

    sendEmailMutation({
      variables: {
        emailAddress: state.formValues.mailAddress,
      },
    });
  }, [
    isRequiredEmailVerification,
    router,
    scenarioId,
    sendEmailMutation,
    state.formValues.mailAddress,
    state.pageStatus,
  ]);

  return {
    state,
    loading,
    sendVerificationCode,
  };
}
